'use client';

// Polyfills must be imported on the client (browser) side only.
import React, {FC, useEffect} from 'react';
import {datadogLogs} from '@datadog/browser-logs';
import {datadogRum} from '@datadog/browser-rum';
import {createSyncStoragePersister} from '@tanstack/query-sync-storage-persister';
import {QueryClient} from '@tanstack/react-query';
import {PersistQueryClientProvider} from '@tanstack/react-query-persist-client';
import Script from 'next/script';
import {ThemeProvider} from 'next-themes';
import {SWRConfig} from 'swr';

import {ErrorBoundary, ErrorBoundaryCaptureInfo} from '@zoefin/design-system';

import '../../../utils/polyfills';

import {
  sessionReplaySampleRate,
  sessionSampleRate,
  telemetrySampleRate,
  traceSampleRate,
} from '../../../constants/datadog';
import HistoryProvider from '../../../stores/HistoryContext';
import localStorageProvider from '../../../utils/localStorageProvider';
import {HUBSPOT_API_HOST} from '../App/constants';

import {LOADING_OVERLAY_STYLE, QUERY_CLIENT_STALE_TIME_MS} from './constants';
import {ProvidersProps} from './types';
import {getLocalStorage, reportRenderingError} from './utils';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: QUERY_CLIENT_STALE_TIME_MS,
    },
  },
});

const persister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' && getLocalStorage(),
});

/**
 * Initializes Datadog RUM + Logs on mount. Must run browser-side because the
 * Datadog browser SDKs access `window`.
 */
const useDatadogInit = (): void => {
  // effect:audited — Datadog SDK init must run in browser, on mount only
  useEffect(() => {
    datadogRum.init({
      applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
      clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
      service: process.env.NEXT_PUBLIC_DATADOG_SERVICE,
      env: process.env.NEXT_PUBLIC_DATADOG_ENVIRONMENT,
      version: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
      silentMultipleInit: true,
      traceSampleRate,
      telemetrySampleRate,
      sessionSampleRate,
      sessionReplaySampleRate,
      trackResources: true,
      trackLongTasks: true,
      trackViewsManually: true,
      trackUserInteractions: true,
      defaultPrivacyLevel: 'mask-user-input',
      trackSessionAcrossSubdomains: false, // This give conflicts with storeContextsAcrossPages 1
      storeContextsAcrossPages: true,
      allowedTracingUrls: [
        (url) =>
          process.env.NEXT_PUBLIC_DATADOG_HOSTS?.split(',').some((host) =>
            url.includes(host),
          ) && !url.includes(HUBSPOT_API_HOST),
      ],
    });

    datadogLogs.init({
      clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
      service: process.env.NEXT_PUBLIC_DATADOG_SERVICE,
      env: process.env.NEXT_PUBLIC_DATADOG_ENVIRONMENT,
      version: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
      silentMultipleInit: true,
      forwardErrorsToLogs: true,
      sessionSampleRate,
      storeContextsAcrossPages: true,
      trackSessionAcrossSubdomains: false, // This give conflicts with storeContextsAcrossPages 1
    });
  }, []);
};

const captureException = (error: Error, info: ErrorBoundaryCaptureInfo) => {
  const componentStack =
    (info?.extra?.errorInfo as {componentStack?: string} | undefined)
      ?.componentStack ?? undefined;
  const renderingError = new Error(error.message);

  renderingError.name = `ReactRenderingError`;
  renderingError.stack = componentStack || error?.stack;
  renderingError.cause = error;

  // Fire-and-forget: surface to Datadog + GTM without blocking ErrorBoundary's sync handler.
  reportRenderingError(error, renderingError).catch(() => undefined);
};

const Providers: FC<ProvidersProps> = ({children, detectedTheme}) => {
  useDatadogInit();

  // React Compiler memoizes this; the manual useCallback was dead weight.
  return (
    <>
      {/*
        Google Tag Manager gtag bootstrap.

        This used to be passed through the design-system's `sanitizeInnerHtml`,
        which was wrong twice over: the string is a hardcoded literal with no
        untrusted input to sanitize, and running an HTML sanitizer over
        JavaScript can silently mangle it. It also made this component — which
        wraps every route — import `isomorphic-dompurify`, pulling jsdom into
        every server bundle, where a CJS/ESM break in its dependency tree
        500'd routes as innocent as /login.

        `next/script` is how the root layout already injects the HubSpot
        bootstrap. `afterInteractive` is safe despite loading after gtag.js
        might: `gtag()` only queues onto `window.dataLayer`, and gtag.js drains
        that queue whenever it arrives, so the two are order-independent.
      */}
      <Script id="gtag-bootstrap" strategy="afterInteractive">
        {`window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());

        gtag('config', 'AW-879980004');`}
      </Script>

      {/* Google Tag Manager (noscript) */}
      <noscript>
        {/*
          Fully sandboxed: this is the no-JS tracking beacon, so it only has to
          issue the request. It needs no scripts, forms, popups or same-origin
          access, and an empty sandbox denies all of them.
        */}
        <iframe
          title="google tag manager"
          src="https://www.googletagmanager.com/ns.html?id=GTM-5FZWLQ6"
          height="0"
          width="0"
          sandbox=""
          style={{display: 'none', visibility: 'hidden'}}
        />
      </noscript>

      {/* White screen that disappears once Next.js renders styles */}
      <div
        id="coverScreen"
        style={LOADING_OVERLAY_STYLE}
        className="loading-overlay"
      />

      <ErrorBoundary
        captureException={captureException}
        environment={process.env.VERCEL_ENV}>
        <HistoryProvider>
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister,
              /*
               * Session-scoped queries opt out with `meta.persist: false`.
               * Without this every successful query is written to
               * localStorage, and a rehydrated result satisfies the query on
               * the next page load — see useAvailabilityRefresh, whose whole
               * job is to refetch on a mount-with-matches.
               */
              dehydrateOptions: {
                /*
                 * `success` is what defaultShouldDehydrateQuery checks. It is
                 * inlined because two @tanstack/query-core versions are
                 * currently resolved (5.101.3 and 5.101.4), so the imported
                 * helper and the Query it is handed come from different copies
                 * and do not typecheck against each other.
                 */
                shouldDehydrateQuery: (query) =>
                  query.meta?.persist !== false &&
                  query.state.status === 'success',
              },
            }}>
            <SWRConfig value={{provider: localStorageProvider}}>
              <ThemeProvider
                defaultTheme={detectedTheme}
                themes={['zoe', 'nerdwallet', 'zoe-old']}>
                {children}
              </ThemeProvider>
            </SWRConfig>
          </PersistQueryClientProvider>
        </HistoryProvider>
      </ErrorBoundary>
    </>
  );
};

export default Providers;
